/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { classnames, TextField } from '@terveystalo/design-system-react-components';
import React, { FC, RefObject, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  id: string;
  type: 'popup' | 'flat'; // Flat is for mobile view. TODO Mobile view not ready.
  isOpen: boolean;
  searchValue: string;
  setIsOpen: (v: boolean) => void;
  onChange: (val: string) => void;
  inputRef?: RefObject<HTMLInputElement>;
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
  focusableItemIds: string[];
};

export const SearchCombobox: FC<Props> = ({
  id,
  type,
  isOpen,
  searchValue,
  setIsOpen,
  onChange,
  inputRef,
  onCancel,
  //loading,
  //disabled,
  children,
  focusableItemIds,
}) => {
  const { t } = useTranslation();
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const listboxId = `${id}-listbox`;

  const clearFocus = useCallback(id => {
    const el = id ? document.getElementById(id) : undefined;
    if (el) {
      el.classList.remove('focused');
      el.setAttribute('aria-selected', 'false');
    }
  }, []);

  const closeSelect = useCallback(() => {
    setIsOpen(false);
    if (onCancel) onCancel();
  }, [onCancel, setIsOpen]);

  useEffect(() => {
    if (type === 'popup' && !isOpen) {
      clearFocus(focusedItemId);
      setFocusedItemId(null);
    }
  }, [clearFocus, focusedItemId, isOpen, type]);

  const setFocus = useCallback(
    (previousId: string | null, id: string | null) => {
      clearFocus(previousId);
      setFocusedItemId(id);
      if (id) {
        const el = document.getElementById(id);
        if (el) {
          el.classList.add('focused');
          el.setAttribute('aria-selected', 'true');
        }
        const container = document.getElementById(listboxId);
        if (el && container) {
          container.scrollTop = el.offsetTop - 0.75 * container.offsetHeight; // scroll in lower half of container
        }
      }
    },
    [clearFocus, listboxId],
  );

  const handleKeydownEvent = useCallback(
    (e, currentFocus) => {
      const currentFocusIndex = currentFocus ? focusableItemIds.indexOf(currentFocus) : null;

      switch (e.key) {
        case 'Tab':
          // Do not prevent default, but close the popup
          if (type === 'popup') {
            closeSelect();
          }
          break;
        case 'Escape':
          if (type === 'popup') {
            closeSelect();
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          }
          // If an option is focused, select it
          if (currentFocus) {
            const el = document.getElementById(currentFocus);
            if (el) {
              setFocusedItemId(null);
              el.click();
            }
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (currentFocusIndex === null || currentFocusIndex === -1) {
            // Nothing in focus -> focus on first option
            setFocus(currentFocus, focusableItemIds[0]);
          } else if (currentFocusIndex === focusableItemIds.length - 1) {
            // Last option in focus -> focus back to only input
            setFocus(currentFocus, null);
          } else {
            // Focus on next option
            setFocus(currentFocus, focusableItemIds[currentFocusIndex + 1]);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentFocusIndex === null || currentFocusIndex === -1) {
            // Nothing in focus -> focus on last option
            setFocus(currentFocus, focusableItemIds[focusableItemIds.length - 1]);
          } else if (currentFocusIndex === 0) {
            // First option in focus -> focus back to only input
            setFocus(currentFocus, null);
          } else {
            // Focus on previous option
            setFocus(currentFocus, focusableItemIds[currentFocusIndex - 1]);
          }
          break;
        default:
        // Other keys control the input normally
      }
    },
    [closeSelect, focusableItemIds, isOpen, setFocus, setIsOpen, type],
  );

  return (
    <div
      role="combobox"
      aria-expanded={isOpen}
      aria-controls={listboxId}
      className={type === 'popup' ? classnames('relative') : classnames('flex', 'flex-col', 'overflow-hidden')}
      css={
        type === 'popup' &&
        css`
          /* z-index is conditional to prevent other instances of this component from howing on top of an open combobox */
          z-index: ${isOpen ? 3 : 1};
        `
      }
    >
      {isOpen && type === 'popup' && (
        <div
          // This is a div with no tab index on purpose
          // The main purpose is to prevent propagation of mouse events when clicking outside to close the popup
          // And as the purpose purely visual, it should be hidden from screen readers
          className={classnames('fixed', 'inset-0')}
          onClick={closeSelect}
          aria-hidden="true"
          css={css`
            z-index: 2;
          `}
        />
      )}
      <div
        className={type === 'popup' ? classnames('relative') : classnames('flex', 'flex-col', 'overflow-hidden')}
        css={
          type === 'popup' &&
          css`
            /* z-index is conditional to prevent other instances of this component from howing on top of an open combobox */
            z-index: ${isOpen ? 3 : 1};
          `
        }
      >
        <TextField
          id="search-filter"
          as="input"
          label={t('search_for_symptoms')}
          icon="SearchLarge"
          value={searchValue}
          onChange={e => {
            if (!isOpen) {
              setIsOpen(true);
            }
            onChange(e.target.value);
          }}
          role="searchbox"
          ref={inputRef}
          aria-controls={listboxId}
          placeholder={t('search')}
          aria-autocomplete="list"
          onKeyDown={e => {
            handleKeydownEvent(e, focusedItemId);
          }}
          onFocus={e => {
            if (type === 'popup') {
              setIsOpen(true);
              onChange(''); // Clear search field on focus for easier search word typing
            } else {
              e.target.select(); // Select the whole word for easier removal
            }
          }}
          onBlur={() => {
            if (!isOpen && type === 'popup' && onCancel) onCancel();
          }}
          aria-activedescendant={focusedItemId || undefined}
        />
        <div
          role="listbox"
          id={listboxId}
          className={classnames(
            'overflow-y-auto',
            type === 'popup'
              ? classnames(
                  'absolute',
                  'p-1',
                  'mt-0.25',
                  isOpen ? 'block' : 'hidden',
                  'bg-white',
                  'rounded-m',
                  'shadow-card',
                )
              : classnames('px-1.5', 'pt-0.5'),
          )}
          css={
            type === 'popup' &&
            css`
              z-index: 3; /* z-index for showing on top of the (focused) search input */
              top: 100%;
              width: 550px;
              max-height: 589px;
            `
          }
        >
          <div className={classnames(type !== 'popup' && 'pb-3')}>{children}</div>
        </div>
      </div>
    </div>
  );
};
